import type { SingleValue } from '@ksyos/design-system';
import type { JSX } from 'react';
import { DateTime } from '@ksyos/date-time';
import { Col, Row, Select } from '@ksyos/design-system';
import { useEffect, useState } from 'react';

type MonthOption = {
    label: string;
    value: number;
};

type YearOption = {
    label: number;
    value: number;
};

type MonthPickerProps = Readonly<{
    maxValue?: Date;
    minValue?: Date;
    onChange?: (newValue: Date) => void;
    selectedDate: Date;
}>;

const allMonthOptions: MonthOption[] = [
    { label: 'Januari', value: 1 },
    { label: 'Februari', value: 2 },
    { label: 'Maart', value: 3 },
    { label: 'April', value: 4 },
    { label: 'Mei', value: 5 },
    { label: 'Juni', value: 6 },
    { label: 'Juli', value: 7 },
    { label: 'Augustus', value: 8 },
    { label: 'September', value: 9 },
    { label: 'Oktober', value: 10 },
    { label: 'November', value: 11 },
    { label: 'December', value: 12 },
];

function formatMonth(date: Date): number {
    return parseInt(DateTime.from(date).format('M'));
}

function formatYear(date: Date): number {
    return parseInt(DateTime.from(date).format('YYYY'));
}

function toYearRange(startYear: number, endYear: number): number[] {
    const years: number[] = [];
    for (let year = startYear; year <= endYear; year++) {
        years.push(year);
    }

    return years;
}

/*
 * This is a temporary solution until CS-19906 is implemented.
 * It was created for CS-20321, so it may not serve all purposes.
 * Because it is a temporary solution, not all texts in the component are translated.
 */
export function MonthPicker(props: MonthPickerProps): JSX.Element {
    const { selectedDate, maxValue, minValue, onChange } = props;
    const [monthOptions, setMonthOptions] = useState<MonthOption[]>([]);
    const [yearOptions, setYearOptions] = useState<YearOption[]>([]);
    const [selectedYear, setSelectedYear] = useState<number>();

    useEffect(() => {
        if (selectedYear !== selectedDate.getFullYear()) {
            setMonths();
            setYears();
            setSelectedYear(selectedDate.getFullYear());
        }
    }, [selectedDate, maxValue, minValue]);

    useEffect(() => {
        if (!monthOptions.some((monthOption) => monthOption.value === selectedDate.getMonth())) {
            handleChangeMonth(monthOptions[0]);
        }
    }, [yearOptions]);

    return (
        <Row
            columnGap={2}
            rowGap={[2, null]}
        >
            <Col span={[12, 6, 4, 3]}>
                <Select
                    onChange={handleChangeMonth}
                    options={monthOptions}
                    placeholder="Maand"
                    value={monthOptions.find((monthOption) => monthOption.value === formatMonth(selectedDate))}
                />
            </Col>
            <Col span={[12, 6, 4, 3]}>
                <Select
                    onChange={handleChangeYear}
                    options={yearOptions}
                    placeholder="Jaar"
                    value={yearOptions.find((yearOption) => yearOption.value === formatYear(selectedDate))}
                />
            </Col>
        </Row>
    );

    function handleChange(month?: number, year?: number): void {
        if (month && year) {
            const newDate = DateTime.from(`${year}-${month}`).startOf('month').toDate();
            onChange?.(newDate);
        }
    }

    function handleChangeMonth(newValue: SingleValue<MonthOption>): void {
        handleChange(newValue?.value, formatYear(selectedDate));
    }

    function handleChangeYear(newValue: SingleValue<YearOption>): void {
        handleChange(formatMonth(selectedDate), newValue?.value);
    }

    function setMonths(): void {
        const now = new Date();

        let minMonth = 1;
        if (minValue && minValue.getFullYear() === formatYear(selectedDate)) {
            minMonth = formatMonth(minValue);
        }

        let maxMonth = 12;
        if (maxValue && maxValue.getFullYear() === formatYear(selectedDate)) {
            maxMonth = formatMonth(maxValue);
        } else if (!maxValue && formatYear(selectedDate) === formatYear(now)) {
            maxMonth = formatMonth(now);
        }

        const nextMonthOptions = allMonthOptions.filter(
            (monthOption) => monthOption.value >= minMonth && monthOption.value <= maxMonth
        );
        setMonthOptions(nextMonthOptions);
    }

    function setYears(): void {
        const now = new Date();
        const minYear = formatYear(minValue ?? now);
        const maxYear = formatYear(maxValue ?? now);
        const yearRange = toYearRange(minYear, maxYear);
        const nextYearOptions = yearRange.map((year) => ({ label: year, value: year }));
        setYearOptions(nextYearOptions);
    }
}
